import sticks from './nn-icon-sticks-grey-signup.svg';
import { post, translate, useDynamicData, useMatchMobile, getClientApiUrl } from '@avensia/excite';
import { Document, type StyleDecorators } from '@avensia/excite-contentful';
import { styled } from '@glitz/react';
import { SecondaryStandingBirdIcon, SittingBirdIcon } from 'nordicnest/shared/icons';
import * as React from 'react';
import { FeedbackControlledButton, useFeedback } from 'webapp/components/button';
import { FieldSize } from 'webapp/components/field-size';
import { TextField, useInputValue } from 'webapp/components/form/text-field';
import {
  Block,
  edgeMarginsDecorator,
  maxContentNormalWidthDecorator,
  ContentWidth,
} from 'webapp/components/page-layout';
import { isLightshop, isNordicnestTheme, SiteTheme, Theme } from 'webapp/theme';
import {
  createMaxSmallViewportDecorator,
  createMinLargeViewportDecorator,
  createMinMediumViewportDecorator,
} from 'webapp/viewport';
import { MemberClubNewsletter } from './memberClubNewsletter';
import { addToDataLayer } from 'webapp/tracking';

export function Newsletter() {
  const [newsletter] = useDynamicData<NordicNest.Api.FooterApiModel>();
  const currentCustomer = useDynamicData<NordicNest.Api.CustomerApiModel>()[0];
  const [email, onChange] = useInputValue();
  const [feedbackState, feedbackPush] = useFeedback();
  const isMobile = useMatchMobile();

  const onSubmit = React.useCallback(
    (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();

      const body: NordicNest.Api.NewsletterModel = {
        email,
        source: 'footer',
        storeKey: '',
      };
      feedbackPush(post(getClientApiUrl('newsletter'), body)).then(() => {
        addToDataLayer({ event: 'registerNewsletter' });
      });
    },
    [email, feedbackPush],
  );

  if (!newsletter) {
    return null;
  }

  return (newsletter.isMemberClubActive && !newsletter.alwaysUseEmailFooter) ? (
    <>{!currentCustomer?.isLoggedIn && <MemberClubNewsletter content={newsletter.memberClubDescription} />}</>
  ) : (
    <>
      <Logos>
        {isNordicnestTheme && <SittingBirdIcon />}
        {isNordicnestTheme && <SecondaryStandingBirdIcon />}
      </Logos>
      <Base onSubmit={onSubmit}>
        <Content>
          <Heading>{translate(t => t.registration.newsletterTitle)}</Heading>
          <Group>
            <Field
              type="email"
              name="email"
              value={email}
              required
              placeholder={translate(t => t.registration.newsletterEmailPlaceholder)}
              onChange={onChange}
              as={Theme.White}
              size={isMobile ? FieldSize.Small : FieldSize.Default}
            />
            <Submit
              type="submit"
              state={feedbackState}
              as={isLightshop ? Theme.Secondary : Theme.Tertiary}
              size={isMobile ? FieldSize.Small : FieldSize.Default}
            >
              {translate(t => t.registration.newsletterButtonLabel)}
            </Submit>
          </Group>
          <Document elements={newsletter.description} styleDecorators={styles} />
        </Content>
      </Base>
    </>
  );
}

const sticksDecorator = styled({
  backgroundImage: `url(${sticks})`,
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'right -90px center',
  backgroundSize: 'auto 110%',
});

const Base = styled.form(
  createMinMediumViewportDecorator({
    padding: { y: t => t.spacing.xxl },
  }),
  {
    backgroundColor: t => t.memberClubNewsletter?.color,
    padding: { xy: t => t.spacing.l },
  },
  isNordicnestTheme ? sticksDecorator : undefined,
);

const Logos = styled(
  Block,
  maxContentNormalWidthDecorator,
  edgeMarginsDecorator,
  createMaxSmallViewportDecorator({ height: 70, justifyContent: 'flex-end' }),
  {
    display: 'flex',
    height: 100,
  },
);

const Content = styled.div({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  margin: { x: 'auto' },
  width: '100%',
  maxWidth: t => (t.id === SiteTheme.NordicNest ? 500 : ContentWidth.Narrow),
  textAlign: 'center',
});

const Heading = styled.h4(
  createMinMediumViewportDecorator({ marginBottom: t => t.spacing.l, fontSize: t => t.memberClubNewsletter?.fontSize }),
  {
    marginBottom: t => t.spacing.s,
    fontWeight: t => t.fontWeight?.bold,
    fontFamily: t => t.fontFamily?.secondary,
    color: t => t.memberClubNewsletter?.text,
  },
);

const Group = styled.div(
  createMaxSmallViewportDecorator({
    flexDirection: 'column',
  }),
  {
    display: 'flex',
    gap: t => t.spacing.xs,
    width: '100%',
    marginBottom: t => t.spacing.m,
  },
);

const Field = styled(TextField, { flexGrow: 1 }, createMinLargeViewportDecorator({}));

const Submit = styled(
  FeedbackControlledButton,
  createMaxSmallViewportDecorator({
    width: '100%',
  }),
  {
    flexShrink: 0,
    alignSelf: 'flex-start',
  },
);

const styles: StyleDecorators = {
  p: styled(createMinLargeViewportDecorator({ marginBottom: 0 }), {
    marginBottom: 0,
    fontSize: t => t.fontSize.xxxs,
    opacity: 0.55,
  }),
};
